import { Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { TAGS, SEARCH_TERMS, INSTAGRAM_URL, CTA_TEXT } from "@/constants/seo";
import { IMAGES } from "@/constants/images";
import { FOREX_PAGES } from "@/constants/forexPages";

export const Footer = () => (
  <footer className="relative border-t border-white/10 bg-[#08080a]">
    <div className="mx-auto max-w-7xl px-6 py-16 sm:px-8 lg:px-12">
      <div className="flex flex-col items-start justify-between gap-10 md:flex-row md:items-center">
        <div className="flex items-center gap-5">
          <div className="overflow-hidden rounded-sm bg-white p-1.5">
            <img
              src={IMAGES.logo}
              alt="Shabz Fazl — Stock Trader Trainer logo"
              className="h-16 w-16 object-contain"
              data-testid="footer-logo"
            />
          </div>
          <div>
            <p className="font-['Playfair_Display'] text-xl text-[#f5f5f0]">Shabz Fazl</p>
            <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
              6 Figure Stock Trading Coach
            </p>
          </div>
        </div>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-cta"
          className="group inline-flex items-center gap-3 border border-[#d4af37]/50 px-7 py-4 font-semibold uppercase tracking-[0.18em] text-sm text-[#d4af37] transition-colors hover:bg-[#d4af37]/10"
        >
          <Instagram className="h-4 w-4" />
          {CTA_TEXT}
        </a>
      </div>

      <div className="my-12 hairline" />

      {/* Topics (50 tags) */}
      <div className="mb-12">
        <h3 className="mb-5 font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
          Coaching Topics
        </h3>
        <div className="flex flex-wrap gap-2">
          {TAGS.map((tag, i) => (
            <span
              key={i}
              data-testid={`tag-${i}`}
              className="border border-white/10 px-3 py-1.5 text-xs text-[#8a8a92] transition-colors hover:border-[#d4af37]/40 hover:text-[#d4af37]"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Popular searches (50 search terms) */}
      <div className="mb-12">
        <h3 className="mb-5 font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
          Popular Searches
        </h3>
        <ul className="grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {SEARCH_TERMS.map((term, i) => (
            <li key={i} data-testid={`search-term-${i}`} className="text-xs leading-relaxed text-[#5f5f66]">
              {term}
            </li>
          ))}
        </ul>
      </div>

      <div className="my-12 hairline" />

      {/* Forex coaching internal links */}
      <div className="mb-12">
        <h3 className="mb-5 font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
          Forex Coaching
        </h3>
        <div className="flex flex-wrap gap-2">
          <Link to="/forex" data-testid="footer-forex-hub" className="border border-[#d4af37]/30 px-3 py-1.5 text-xs capitalize text-[#d4af37] transition-colors hover:bg-[#d4af37]/10">
            All Forex Coaching
          </Link>
          {FOREX_PAGES.map((p) => (
            <Link
              key={p.slug}
              to={`/forex/${p.slug}`}
              data-testid={`footer-forex-${p.slug}`}
              className="border border-white/10 px-3 py-1.5 text-xs capitalize text-[#8a8a92] transition-colors hover:border-[#d4af37]/40 hover:text-[#d4af37]"
            >
              {p.key}
            </Link>
          ))}
        </div>
      </div>

      <div className="my-10 hairline" />

      <div className="flex flex-col gap-6 text-xs text-[#5f5f66] md:flex-row md:items-center md:justify-between">
        <p>&copy; {new Date().getFullYear()} Shabz Fazl. All rights reserved.</p>
        <p className="max-w-xl leading-relaxed">
          Trading involves substantial risk and is not suitable for everyone. Coaching is
          educational and paid only; no profit or income is guaranteed. All enquiries via Instagram DM.
        </p>
      </div>

      {/* Powered by Amerishop — subtle */}
      <div className="mt-8 flex items-center justify-center gap-2.5 border-t border-white/5 pt-6">
        <span className="font-['IBM_Plex_Mono'] text-[9px] uppercase tracking-[0.28em] text-[#4a4a50]">
          Powered by
        </span>
        <a
          href="https://amerishop.biz"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="powered-by-amerishop"
          className="group inline-flex items-center rounded bg-white/95 px-2 py-1 transition-transform duration-300 hover:scale-[1.03]"
          aria-label="Powered by Amerishop — visit amerishop.biz"
        >
          <img
            src="/amerishop-logo.webp"
            alt="Amerishop.biz"
            data-testid="amerishop-logo"
            className="h-4 w-auto sm:h-5"
          />
        </a>
      </div>
    </div>
  </footer>
);
